import PropTypes from 'prop-types';

// material-ui
import { useTheme, useMediaQuery, Box, Drawer, LinearProgress, Typography } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import MenuCard from './MenuCard';
import { drawerWidth } from 'store/constant';
import { gql, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router';
import NavItem from './MenuList/NavItem';
import NavGroup from './MenuList/NavGroup';
import { GET_VERIFICATION_FEATURE } from 'Components/RoleEmailPage';
import React from 'react';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { IconLogout, IconUserCheck, IconUsers, IconQrcode } from '@tabler/icons'; // Combined icon imports
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import EmailIcon from '@mui/icons-material/Email';
import {
    CorporateFareRounded,
    PanoramaHorizontalSelectOutlined,
    SwitchAccount,
    TableBarRounded,
    EmailOutlined,
} from '@mui/icons-material';
// constant
const icons = {
    IconLogout,
    IconUserCheck,
    IconUsers,
    VerifiedUserIcon,
    EmailIcon,
    IconQrcode,
    HandshakeOutlinedIcon,
    SwitchAccount,
    LocalPrintshopIcon,
    PanoramaHorizontalSelectOutlined,
    TableBarRounded,
    CorporateFareRounded,
    EmailOutlined,
};
// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
    const theme = useTheme();
    const { eventId } = useParams();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const { data, loading, error } = useQuery(GET_ME);
    const [roles, setRoles] = React.useState([]);
    const { data: featureData, loading: featureLoading } = useQuery(GET_VERIFY_ROLES, {
        variables: { eventId },
    });
    React.useEffect(() => {
        setRoles(featureData?.getverifyroles);
    }, [featureData]);

    const showVerification =
        (data?.me?.permissions?.includes('admin') ?? false) && Array.isArray(roles) && roles.length > 0;

    if (featureLoading) {
        return (
            <>
                <LinearProgress />
            </>
        );
    }
    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography>{error.message}</Typography>;
    }
    const drawer = (
        <>
            {/* Removed duplicate logo - logo is now only in Header */}
            <BrowserView>
                <PerfectScrollbar
                    component='div'
                    style={{
                        height: 'calc(100vh - 56px)',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                    }}
                    className='sidebar-gradient'
                    options={{
                        suppressScrollX: true,
                        useBothWheelAxes: false,
                    }}
                >
                    <MenuList />
                    <RoleList />
                    <MenuCard />
                    {showVerification && <Verification eventId={eventId} />}
                    {data?.me?.permissions?.includes('emailtracking') && <EmailTracking permissions={data?.me} />}
                    {(data?.me?.permissions?.includes('admin') || data?.me?.permissions?.includes('superadmin')) && (
                        <EmailTemplateMenu eventId={eventId} />
                    )}
                    {data?.me?.permissions?.includes('leadgen') && <LeadGeneration permissions={data?.me} />}
                    {data?.me?.permissions?.includes('showsponsors') && <ShowSponsorSystem permissions={data?.me} />}
                    {data?.me?.permissions?.includes('showcorporates') && (
                        <ShowCorporateSystem permissions={data?.me} />
                    )}
                    {data?.me?.permissions?.includes('showexhibitors') && (
                        <ShowExhibitorSystem permissions={data?.me} />
                    )}
                    {data?.me?.permissions?.includes('generatePromo') && <GeneratePromoCode permissions={data?.me} />}
                    {data?.me?.permissions?.includes('crew') && <Crew />}
                    {/* Role Switch menu hidden as per requirements */}
                    {/* {data?.me?.permissions?.includes('role-switcher') && <RoleSwitcher />} */}
                    {data?.me?.permissions?.includes('bulkprinting') && <BulkPrinting />}

                    <LogOut />
                </PerfectScrollbar>
            </BrowserView>
            <MobileView>
                <Box sx={{ px: 2 }}>
                    <MenuList />
                    <RoleList />
                    {!!showVerification && <Verification eventId={eventId} />}
                    {data?.me?.permissions?.includes('emailtracking') && <EmailTracking permissions={data?.me} />}
                    {(data?.me?.permissions?.includes('admin') || data?.me?.permissions?.includes('superadmin')) && (
                        <EmailTemplateMenu eventId={eventId} />
                    )}
                    {data?.me?.permissions?.includes('leadgen') && <LeadGeneration permissions={data?.me} />}
                    {data?.me?.permissions?.includes('showsponsors') && <ShowSponsorSystem permissions={data?.me} />}
                    {data?.me?.permissions?.includes('showcorporates') && (
                        <ShowCorporateSystem permissions={data?.me} />
                    )}
                    {data?.me?.permissions?.includes('showexhibitors') && (
                        <ShowExhibitorSystem permissions={data?.me} />
                    )}
                    {data?.me?.permissions?.includes('generatePromo') && <GeneratePromoCode permissions={data?.me} />}
                    {data?.me?.permissions?.includes('crew') && <Crew />}
                    {/* Role Switch menu hidden as per requirements */}
                    {/* {data?.me?.permissions?.includes('role-switcher') && <RoleSwitcher />} */}
                    {data?.me?.permissions?.includes('bulkprinting') && <BulkPrinting />}
                    <LogOut />
                    <MenuCard />
                </Box>
            </MobileView>
        </>
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box
            component='nav'
            sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }}
            aria-label='mailbox folders'
        >
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor='left'
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: 'linear-gradient(180deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
                        backgroundSize: '200% 200%',
                        animation: 'gradientShift 15s ease infinite',
                        color: 'white',
                        borderRight: 'none',
                        boxShadow: '8px 0 30px rgba(0,0,0,0.15), inset -1px 0 0 rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        position: 'fixed',
                        height: '100vh',
                        overflow: 'hidden',
                        top: '56px',
                        height: 'calc(100vh - 56px)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                                'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
                            pointerEvents: 'none',
                            zIndex: 0,
                        },
                        '& > *': {
                            position: 'relative',
                            zIndex: 1,
                        },
                    },
                }}
                ModalProps={{ keepMounted: true }}
                color='inherit'
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    window: PropTypes.object,
};

export default Sidebar;

export function RoleList() {
    const { data, loading, error } = useQuery(GET_ROLES, {
        onError: e => console.log(JSON.stringify(e, null, 2)),
    });

    if (loading) {
        return [];
    }

    if (error) {
        return [{ id: 'error', title: 'Error', type: 'item', url: '', target: false }];
    }

    const rolesData = data?.event?.roles?.split(',') ?? [];

    const item = {
        id: 'roles',
        title: 'Roles List',
        type: 'group',
        children: rolesData.map((item, i) => ({
            id: i,
            title: item.charAt(0).toUpperCase() + item.slice(1),
            type: 'item',
            url: `${item}/data`,
            icon: icons.IconUserCheck,
            breadcrumbs: false,
        })),
    };

    const level = 1;

    return <NavGroup item={item} level={level} />;
}

function Crew() {
    const level = 3;
    const crew = {
        id: 'crew',
        title: 'Features',
        type: 'group',
        children: [
            {
                id: 'reception',
                title: 'Reception Users',
                type: 'item',
                url: 'crew/RECEPTION',
                icon: icons.IconUsers,
                breadcrumbs: false,
            },
            {
                id: 'printer',
                title: 'Printer Users',
                type: 'item',
                url: 'crew/PRINTER',
                icon: icons.IconUsers,
                breadcrumbs: false,
            },
            {
                id: 'entryGate',
                title: 'Entry Gate Scanner',
                type: 'item',
                url: 'crew/ENTRY_SCAN',
                icon: icons.IconUsers,
                breadcrumbs: false,
            },
            {
                id: 'exitGate',
                title: 'Exit Gate Scanner',
                type: 'item',
                url: 'crew/EXIT_SCAN',
                icon: icons.IconUsers,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={crew} level={level} />;
}
function RoleSwitcher() {
    const roleSwitcher = {
        id: 'roleSwitcher',
        title: 'Role Switcher',
        type: 'group',
        children: [
            {
                id: 'roleSwitcher',
                title: 'Role Switch',
                type: 'item',
                url: 'roleSwitcher',
                icon: icons.SwitchAccount,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={roleSwitcher} />;
}
function BulkPrinting() {
    const bulkprinting = {
        id: 'bulkprinting',
        title: 'Bulk Printing',
        type: 'group',
        children: [
            {
                id: 'bulkprinting',
                title: 'Bulk Print',
                type: 'item',
                url: 'bulkprinting',
                icon: icons.LocalPrintshopIcon,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={bulkprinting} />;
}
function Verification({ eventId }) {
    const { data, loading, error } = useQuery(GET_VERIFY_ROLES, {
        variables: { eventId },
        onError: e => console.log(JSON.stringify(e, null, 2)),
    });

    const verifyRoles = data?.getverifyroles || [];

    const children = verifyRoles.map((item, i) => ({
        id: i + 6,
        title: `Verify ${item.charAt(0).toUpperCase() + item.slice(1)}`,
        type: 'item',
        url: `verify/${item}`,
        icon: icons.VerifiedUserIcon,
        breadcrumbs: false,
    }));

    const verification = {
        id: 'verification',
        title: 'Registration Verification',
        type: 'group',
        children: children,
    };
    const level = 2;
    return <NavGroup item={verification} level={level} />;
}
function EmailTracking() {
    const emailTracking = {
        id: 'emailTracking',
        title: 'Email Tracking',
        type: 'group',
        children: [
            {
                id: 'emailTracking',
                title: 'Email Tracking',
                type: 'item',
                url: 'emailtracking',
                icon: icons.EmailIcon,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={emailTracking} />;
}
function EmailTemplateMenu({ eventId }) {
    const emailTemplate = {
        id: 'emailTemplates',
        title: 'Email Templates',
        type: 'group',
        children: [
            {
                id: 'emailTemplates',
                title: 'Email Templates',
                type: 'item',
                url: 'email-templates',
                icon: icons.EmailOutlined,
                breadcrumbs: false,
            },
        ],
    };

    return <NavGroup item={emailTemplate} />;
}
function LeadGeneration() {
    const leadGeneration = {
        id: 'leadGeneration',
        title: 'Lead Generation',
        type: 'group',
        children: [
            {
                id: 'leadGeneration',
                title: 'Lead Generation',
                type: 'item',
                url: 'leadgeneration',
                icon: icons.IconQrcode,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={leadGeneration} />;
}
function ShowSponsorSystem() {
    const showSponsors = {
        id: 'showSponsors',
        title: 'Sponsors List',
        type: 'group',
        children: [
            {
                id: 'showSponsors',
                title: 'Sponsors List',
                type: 'item',
                url: 'showsponsors',
                icon: icons.HandshakeOutlinedIcon,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={showSponsors} />;
}
function ShowCorporateSystem() {
    const showCorporate = {
        id: 'showCorporate',
        title: 'Corporates List',
        type: 'group',
        children: [
            {
                id: 'showCorporate',
                title: 'Corporates List',
                type: 'item',
                url: 'showcorporates',
                icon: icons.CorporateFareRounded,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={showCorporate} />;
}
function ShowExhibitorSystem() {
    const showExhibitor = {
        id: 'showExhibitor',
        title: 'Exhibitors List',
        type: 'group',
        children: [
            {
                id: 'showExhibitor',
                title: 'Exhibitors List',
                type: 'item',
                url: 'showexhibitors',
                icon: icons.TableBarRounded,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={showExhibitor} />;
}
function GeneratePromoCode() {
    const showPromoCode = {
        id: 'generatePromo',
        title: 'Promo Code ',
        type: 'group',
        children: [
            {
                id: 'generatePromo',
                title: 'Manage Promo Codes',
                type: 'item',
                url: 'generatePromo',
                icon: icons.PanoramaHorizontalSelectOutlined,
                breadcrumbs: false,
            },
        ],
    };
    return <NavGroup item={showPromoCode} />;
}
function LogOut() {
    const logout = {
        id: 'logout',
        title: 'Logout',
        type: 'group',
        children: [
            {
                id: 'logout',
                title: 'Logout',
                type: 'item',
                url: 'logout',
                icon: icons.IconLogout,
                breadcrumbs: false,
            },
        ],
    };
    const level = 4;
    return <NavGroup item={logout} level={level} />;
}

export const GET_ROLES = gql`
    query {
        event {
            roles
        }
    }
`;
export const GET_VERIFY_ROLES = gql`
    query ($eventId: String!) {
        getverifyroles(eventId: $eventId)
    }
`;
const GET_ME = gql`
    query {
        me {
            permissions
        }
    }
`;
